import { useState } from 'react';
import { IconBell, IconChevronDown, IconChevronRight, IconFileTypePdf, IconLayoutSidebarRight, IconPrinter, IconSearch, IconSparkles } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { useWide } from '../sales/useWide';
import { ARTICLES, MODS, SOPS, SVC, edges, filterArticles, filterSops, fmtIso, geometry, modK, nodeRows, raciCell, raciSeats, seatK, sopAnswer, stepColor, stepsOf, type Article, type Sop } from './sop';
import { useT } from '@hana/i18n';
import './sop.i18n';
import s from './sop.module.css';

// Mint-SOP (1536 / 1920): Quy trình & hướng dẫn · tab SOP (lưu đồ · RACI · KPI) và Hướng dẫn sử dụng. Chỉ đọc, nút ghi báo toast.

type Tab = 'sop' | 'help';
type Sec = 'flow' | 'raci' | 'kpi';
type Log = [boolean, string][];

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const chipStyle = (k: string) => ({ background: `color-mix(in srgb, ${SVC[k] ?? 'var(--d-gray)'} 18%, transparent)` });

export function SOP() {
  const tt = useT();
  const toastFn = useToast();
  const toast = (msg: string) => toastFn({ msg });
  const wide = useWide();
  const qs = new URLSearchParams(location.search);
  const state: ScreenState = readState() ?? 'ready';
  const [tab, setTab] = useState<Tab>(qs.get('tab') === 'help' ? 'help' : 'sop');
  const [q, setQ] = useState('');
  const [dense, setDense] = useState(false);
  const [right, setRight] = useState(false);
  const [sopKey, setSopKey] = useState(qs.get('sop') ?? 'coo');
  const [sec, setSec] = useState<Sec>('flow');
  const [horiz, setHoriz] = useState(true);
  const [scope, setScope] = useState(false);
  const [node, setNode] = useState<string | null>(null);
  const [ptab, setPtab] = useState<'act' | 'ask' | 'rel'>('act');
  const [art, setArt] = useState<string | null>(null);
  const [coll, setColl] = useState<Record<string, boolean>>({});
  const [logs, setLogs] = useState<Record<string, Log>>({});
  const [ask, setAsk] = useState('');
  const soon = (m: string) => () => toast(tt('sop.soon', { m }));

  const isSop = tab === 'sop';
  const sopList = filterSops(q);
  const artList = filterArticles(q);
  const S: Sop = SOPS.find((x) => x.k === sopKey) ?? SOPS[0]!;
  const ctx = qs.get('ctx') ?? 'none';
  const A: Article | undefined = ARTICLES.find((a) => a.id === art) ?? (ctx !== 'none' ? ARTICLES.find((a) => a.mod === ctx) : undefined) ?? artList[0];
  const ready = state === 'ready' || state === 'noperm';
  const panelOn = ready && (wide || right || (isSop ? node !== null : false));
  const geo = geometry(S, horiz, (wide ? 1920 : 1536) - 248 - 268 - 40 - (panelOn && !wide ? 332 : 0) - 120);
  const nodeSel = node ? S.n.find((n) => n[0] === node) : undefined;
  const logKey = isSop ? `sop:${S.k}` : `help:${A?.id ?? ''}`;
  const log = logs[logKey] ?? [];
  const pushAsk = (v: string) => { if (!v.trim()) return; setLogs((o) => ({ ...o, [logKey]: [...(o[logKey] ?? []), [true, v], [false, isSop ? sopAnswer(S, v) : `Bài “${A?.t ?? ''}”: ${A?.sum ?? ''}`]] })); setAsk(''); };
  const pickNode = (id: string) => { setNode(id); setPtab('act'); };
  const rowH = dense ? 34 : 40;

  const col1 = () => {
    if (state === 'loading') return <Skeleton rows={8} />;
    if (!isSop) {
      return MODS.map(([mk, ml]) => {
        const arr = artList.filter((a) => a.mod === mk);
        if (!arr.length) return null;
        const closed = !!coll[mk] && !q;
        return (
          <div key={mk}>
            <button type="button" className={s.head} aria-expanded={!closed} onClick={() => setColl((o) => ({ ...o, [mk]: !o[mk] }))}>
              {closed ? <IconChevronRight size={14} aria-hidden="true" /> : <IconChevronDown size={14} aria-hidden="true" />}
              <span>{ml.toUpperCase()}</span><span className={s.n}>{arr.length}</span>
            </button>
            {!closed && arr.map((a) => <button key={a.id} type="button" className={s.item} aria-current={A?.id === a.id} style={{ minHeight: rowH }} onClick={() => { setArt(a.id); setPtab('ask'); }}><span className={s.itx}><span className={s.k}>{a.t}</span><span className={s.v}>{a.cat}</span></span></button>)}
          </div>
        );
      });
    }
    if (state === 'empty') return null;
    return sopList.map((x) => {
      const hit = q ? x.n.find((n) => n[5].toLowerCase().includes(q.toLowerCase())) : undefined;
      return (
        <button key={x.k} type="button" className={s.item} aria-current={x.k === S.k} onClick={() => { setSopKey(x.k); setNode(null); }}>
          <span className={s.chip} style={chipStyle(x.k)} aria-hidden="true">{x.k.toUpperCase()}</span>
          <span className={s.itx}><span className={s.k}>{x.t.replace(/^Quy trình /, '')}</span><span className={s.v}>{hit ? tt('sop.hit', { id: hit[0], name: hit[5] }) : tt('sop.sopMeta', { c: x.c, n: x.n.length, v: x.v })}</span></span>
        </button>
      );
    });
  };

  const flow = () => {
    const es = edges(S, geo, horiz);
    const legend = [...stepsOf(S).map((k) => ({ k, c: stepColor(S, k) })), { k: tt('sop.external'), c: 'var(--bd)' }];
    return (
      <>
        <div className={s.legend}>{legend.map((g) => <span key={g.k}><i style={{ background: g.c }} />{g.k}</span>)}</div>
        <div className={s.canvas} style={{ width: geo.fw, height: geo.fh }}>
          {S.l.map((l, i) => {
            const st = horiz ? { left: 0, top: i * 88 + 4, width: geo.fw, height: 80 } : { left: i * geo.lw + 4, top: 0, width: geo.lw - 8, height: geo.fh };
            return <div key={l} className={`${s.lane} ${i % 2 ? '' : s.laneOdd}`} style={st}><span className={`${s.laneK} ${l.startsWith('EXT') ? s.ext : ''}`} style={horiz ? { top: 28 } : undefined}>{seatK(l)}</span></div>;
          })}
          <svg aria-hidden="true" width={geo.fw} height={geo.fh} className={s.svg}>
            <defs>
              {[['mkN', 'var(--lb)'], ['mkOk', 'var(--ok)'], ['mkWarn', 'var(--warn)']].map(([id, c]) => <marker key={id} id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill={c} /></marker>)}
            </defs>
            {es.map((e, i) => { const c = e.tone === 'ok' ? 'var(--ok)' : e.tone === 'warn' ? 'var(--warn)' : 'var(--lb)'; const mk = e.tone === 'ok' ? 'mkOk' : e.tone === 'warn' ? 'mkWarn' : 'mkN'; return <path key={i} d={e.d} fill="none" stroke={c} strokeWidth={e.label ? 1.6 : 1.3} strokeDasharray={e.dash ? '4 3' : undefined} markerEnd={`url(#${mk})`} />; })}
            {es.filter((e) => e.label).map((e, i) => <text key={i} x={e.lx} y={e.ly} fontSize="11" fontWeight="550" textAnchor="middle" fill={e.tone === 'ok' ? 'var(--ok)' : e.tone === 'warn' ? 'var(--warn)' : 'var(--lb)'}>{e.label}</text>)}
          </svg>
          {S.n.map((n) => {
            const p = geo.pos(n);
            const [id, kind, step, lane, , short] = n;
            const tag = kind === 'decision' ? tt('sop.tagDec') : kind === 'external' ? tt('sop.tagExt') : kind === 'post' ? tt('sop.tagPost') : '';
            return (
              <button key={id} type="button" className={`${s.node} ${kind === 'external' ? s.nExt : ''} ${kind === 'decision' ? s.nDec : ''} ${kind === 'post' ? s.nPost : ''}`} aria-pressed={node === id} aria-label={`${id} ${short} · ${seatK(lane)}`}
                style={{ left: Math.round(p.cx - p.w / 2), top: Math.round(p.cy - p.h / 2), width: p.w, height: p.h }} onClick={() => pickNode(id)}>
                <span className={s.nId} style={{ color: kind === 'external' ? 'var(--lb)' : stepColor(S, step) }}>{id} {tag}</span>
                <span className={s.nK}>{short}</span>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const raci = () => {
    const seats = raciSeats(S);
    const cols = `minmax(220px, 1.6fr) repeat(${seats.length}, minmax(86px, 1fr))`;
    return (
      <div role="table" aria-label={tt('sop.raciAria')} className={s.table}>
        <div role="row" className={`${s.tr} ${s.th}`} style={{ gridTemplateColumns: cols }}><span role="columnheader">{tt('sop.activity')}</span>{seats.map((c) => <span key={c} role="columnheader" className={s.c}>{seatK(c)}</span>)}</div>
        {S.n.filter((n) => n[6] && Object.keys(n[6]).length).map((n) => (
          <div key={n[0]} role="row" className={s.tr} style={{ gridTemplateColumns: cols, minHeight: rowH }}>
            <button type="button" role="rowheader" className={s.rh} onClick={() => pickNode(n[0])}><b>{n[0]}</b> {n[5]}</button>
            {seats.map((c) => { const v = raciCell(n, c); return <span key={c} role="cell" className={`${s.c} ${v ? s[`r${v[0]}`] : ''}`}>{v}</span>; })}
          </div>
        ))}
      </div>
    );
  };

  const kpi = () => (
    <div role="table" aria-label={tt('sop.kpiAria')} className={s.table}>
      <div role="row" className={`${s.tr} ${s.th}`} style={{ gridTemplateColumns: '36px minmax(0,2.4fr) 90px minmax(0,1.6fr)' }}><span>#</span><span>{tt('sop.kpiMeasure')}</span><span>{tt('sop.target')}</span><span>{tt('sop.sanction')}</span></div>
      {S.kp.map((k, i) => (
        <div key={i} role="row" className={s.tr} style={{ gridTemplateColumns: '36px minmax(0,2.4fr) 90px minmax(0,1.6fr)', minHeight: rowH }}>
          <span className={s.lb}>{i + 1}</span><span><span>{k[0]}</span>{k[1] && <span className={s.sub}>{k[1]}</span>}</span><b>{k[2]}</b><span className={s.tx2}>{k[3]}</span>
        </div>
      ))}
    </div>
  );

  const sopView = (
    <div className={s.read}>
      <div className={s.rHead}>
        <span className={s.chip} style={chipStyle(S.k)} aria-hidden="true">{S.k.toUpperCase()}</span>
        <div className={s.rTitle}>
          <h2>{S.t}</h2>
          <div className={s.meta}><b>{S.c}</b><span>v{S.v}</span><span>{tt('sop.effective')} {fmtIso(S.e)}</span><span>{tt('sop.owner')} {S.o}</span><span className={s.draft}>{S.st === 'draft' ? tt('sop.draftWait') : tt('sop.inForce')}</span></div>
        </div>
        <button type="button" className={s.ib} aria-label={tt('sop.print')} title={tt('sop.print')} onClick={() => toast(tt('sop.printDone', { c: S.c }))}><IconPrinter size={16} aria-hidden="true" /></button>
        <Button variant="ask" icon={<IconSparkles size={14} aria-hidden="true" />} onClick={() => { setRight(true); setPtab('ask'); }}>{tt('sop.askHana')}</Button>
      </div>
      {S.sc && <div className={s.scope}><div className={scope ? undefined : s.clamp}>{S.sc}</div><button type="button" className={s.lnk} onClick={() => setScope((o) => !o)}>{scope ? tt('sop.collapse') : tt('sop.scope')}</button></div>}
      <div className={s.secBar}>
        <Segmented label={tt('sop.secAria')} value={sec} onChange={setSec} items={[{ key: 'flow', label: <>{tt('sop.flow')} <span className={s.cnt}>{tt('sop.steps', { n: S.n.length })}</span></> }, { key: 'raci', label: tt('sop.raci') }, { key: 'kpi', label: <>{tt('sop.kpiTab')} <span className={s.cnt}>{S.kp.length}</span></> }]} />
        {sec === 'flow' && <Segmented kind="toggle" label={tt('sop.dirAria')} value={horiz ? 'h' : 'v'} onChange={(v) => setHoriz(v === 'h')} items={[{ key: 'h', label: tt('sop.horiz') }, { key: 'v', label: tt('sop.vert') }]} />}
      </div>
      <div className={s.body}>{sec === 'flow' ? flow() : sec === 'raci' ? raci() : kpi()}</div>
    </div>
  );

  const helpView = A && (
    <div className={s.read}>
      <div className={s.rHead}>
        <div className={s.rTitle}>
          <div className={s.meta}><span className={s.tag}>{modK(A.mod)}</span><span>{A.cat}</span><span>v{A.ver}</span><span>{tt('sop.updated')} {A.upd}</span></div>
          <h2>{A.t}</h2>
        </div>
        <Button variant="ask" icon={<IconSparkles size={14} aria-hidden="true" />} onClick={() => { setRight(true); setPtab('ask'); }}>{tt('sop.askHana')}</Button>
      </div>
      <p className={s.sum}>{A.sum}</p>
      {A.vid && <button type="button" className={s.video} onClick={() => toast(tt('sop.playing'))}>{tt('sop.video')}</button>}
      <ol className={s.steps}>{A.steps.map((t) => <li key={t}>{t}</li>)}</ol>
      <div className={s.kws}>{A.kw.map((k) => <span key={k} className={s.tag}>{k}</span>)}</div>
    </div>
  );

  const main = () => {
    if (state === 'loading') return <Skeleton rows={8} />;
    if (state === 'error') return <StateView state="error" title={isSop ? tt('sop.errSop') : tt('sop.errHelp')} desc={isSop ? tt('sop.errSopDesc') : tt('sop.errHelpDesc')} action={<Button onClick={() => location.reload()}>{tt('sop.retry')}</Button>} />;
    if (state === 'empty' || (!isSop && !A)) return <StateView state="empty" title={isSop ? tt('sop.emptySop') : tt('sop.emptyHelp')} desc={isSop ? tt('sop.emptySopDesc') : tt('sop.emptyHelpDesc')} />;
    return isSop ? sopView : helpView;
  };

  const panel = (
    <aside className={s.panel} aria-label={tt('sop.rightAria')}>
      <Segmented label={tt('sop.panelAria')} value={ptab === 'rel' && isSop ? 'act' : ptab} onChange={setPtab} items={isSop ? [{ key: 'act', label: tt('sop.actDesc') }, { key: 'ask', label: tt('sop.askHana') }] : [{ key: 'ask', label: tt('sop.askHana') }, { key: 'rel', label: tt('sop.related') }]} />
      {isSop && ptab === 'act' && (nodeSel ? (
        <div className={s.nd}>
          <b>{nodeSel[0]} · {nodeSel[7]?.[0]?.split(';')[0] ?? nodeSel[5]}</b>
          <span className={s.lb}>{nodeSel[2] ? tt('sop.appStep', { s: nodeSel[2] }) : tt('sop.outApp')} · {tt('sop.col')} {nodeSel[4]} · {seatK(nodeSel[3])}</span>
          {nodeRows(nodeSel).map((r) => <div key={r.k}><span className={s.lb}>{r.k}</span><div>{r.v}</div></div>)}
          {(['R', 'A', 'C', 'I'] as const).filter((r) => nodeSel[6]?.[r]?.length).map((r) => <div key={r}><span className={s.lb}>{r}</span><div>{nodeSel[6][r]!.map(seatK).join(', ')}</div></div>)}
          {S.g.filter((e) => e[0] === nodeSel[0] && S.n.some((n) => n[0] === e[1])).map((e) => <button key={e[1] + e[2]} type="button" className={s.lnk} onClick={() => pickNode(e[1])}>→ {e[1]}{e[2] ? ` · ${e[2]}` : ''}</button>)}
        </div>
      ) : <div className={s.lb}>{tt('sop.nodeHint')}</div>)}
      {ptab === 'rel' && !isSop && <div className={s.nd}>{ARTICLES.filter((a) => A && a.mod === A.mod && a.id !== A.id).slice(0, 5).map((a) => <button key={a.id} type="button" className={s.item} onClick={() => setArt(a.id)}><span className={s.itx}><span className={s.k}>{a.t}</span><span className={s.v}>{a.cat}</span></span></button>)}</div>}
      {(ptab === 'ask' || (!isSop && ptab !== 'rel')) && (
        <div className={s.nd}>
          <span className={s.lb}>{isSop ? S.c : A?.t}</span>
          {log.map(([me, t], i) => <div key={i} className={me ? s.me : s.bot}>{t}</div>)}
          <div className={s.chips}>{(isSop ? [tt('sop.q1'), tt('sop.q2'), tt('sop.q3')] : [tt('sop.q4'), tt('sop.q5'), tt('sop.q6')]).map((k) => <button key={k} type="button" onClick={() => pushAsk(k)}>{k}</button>)}</div>
          <form className={s.askRow} onSubmit={(e) => { e.preventDefault(); pushAsk(ask); }}><input aria-label={tt('sop.askHana')} placeholder={isSop ? tt('sop.askPhSop') : tt('sop.askPhHelp')} value={ask} onChange={(e) => setAsk(e.target.value)} /><Button variant="primary" type="submit">{tt('sop.send')}</Button></form>
        </div>
      )}
    </aside>
  );

  return (
    <Page kind="table" dock={wide && panelOn ? panel : undefined}>
      <PageHeader
        title={tt('sop.title')}
        tabs={<Segmented label={tt('sop.typeAria')} value={tab} onChange={(v) => { setTab(v); setQ(''); setPtab(v === 'sop' ? 'act' : 'ask'); }} items={[{ key: 'sop', label: <>{tt('sop.tabSop')} <span className={s.cnt}>{SOPS.length}</span></> }, { key: 'help', label: <>{tt('sop.tabHelp')} <span className={s.cnt}>{ARTICLES.length}</span></> }]} />}
        actions={
          <>
            <label className={s.search}><IconSearch size={16} aria-hidden="true" /><input aria-label={tt('sop.searchAria')} placeholder={tt('sop.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} /></label>
            <Segmented kind="toggle" label={tt('sop.densAria')} value={dense ? 'g' : 'c'} onChange={(v) => setDense(v === 'g')} items={[{ key: 'c', label: tt('sop.dense0') }, { key: 'g', label: tt('sop.dense1') }]} />
            <HeaderTool label={tt('sop.bellAria')} icon={IconBell} n={2} onClick={soon(tt('sop.bell'))} />
            {!wide && <HeaderTool label={tt('sop.rightAria')} icon={IconLayoutSidebarRight} active={right} onClick={() => setRight((o) => !o)} />}
            {isSop && <Button variant="primary" icon={<IconFileTypePdf size={16} aria-hidden="true" />} onClick={() => toast(tt('sop.pdfDone', { c: S.c }))}>{tt('sop.pdf')}</Button>}
          </>
        }
      />
      <div className={s.split}>
        <section className={s.col1} aria-label={isSop ? tt('sop.listAria') : tt('sop.artAria')}>
          {q && state === 'ready' && <div className={s.lb}>{isSop ? tt('sop.nSop', { n: sopList.length }) : tt('sop.nArt', { n: artList.length })}</div>}
          {col1()}
          {q && state === 'ready' && (isSop ? sopList.length : artList.length) === 0 && <div className={s.lb}>{tt('sop.noMatch', { q })}</div>}
        </section>
        <section className={s.col2} aria-label={isSop ? tt('sop.sec') : tt('sop.secHelp')}>{main()}</section>
        {panelOn && !wide && panel}
      </div>
    </Page>
  );
}
